import type { ConsentAction, ConsentState, CookieCategory } from "./types";

const COOKIE_NAME = "cookieguard_consent";
const MAX_AGE_SECONDS = 365 * 24 * 60 * 60; // 12 months
const ALL_CATEGORIES: CookieCategory[] = ["necessary", "functional", "analytics", "marketing"];

export function readConsent(): ConsentState | null {
	const raw = getCookieValue(COOKIE_NAME);
	if (!raw) return null;

	try {
		const parsed: unknown = JSON.parse(decodeURIComponent(raw));
		if (!isValidConsentState(parsed)) return null;

		// Check expiration (12 months from UTC timestamp)
		const twelveMonthsMs = MAX_AGE_SECONDS * 1000;
		if (Date.now() - parsed.utc > twelveMonthsMs) {
			deleteConsentCookie();
			return null;
		}

		return parsed;
	} catch {
		return null;
	}
}

export function writeConsent(
	apiKey: string,
	choices: Record<CookieCategory, boolean>,
	action: ConsentAction,
	configVersion: string,
): ConsentState {
	const state: ConsentState = {
		stamp: apiKey,
		necessary: true,
		functional: choices.functional ?? false,
		analytics: choices.analytics ?? false,
		marketing: choices.marketing ?? false,
		action,
		version: configVersion,
		utc: Date.now(),
	};

	const encoded = encodeURIComponent(JSON.stringify(state));
	document.cookie = `${COOKIE_NAME}=${encoded}; path=/; max-age=${MAX_AGE_SECONDS}; SameSite=Lax`;

	return state;
}

export function deleteConsentCookie(): void {
	document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`;
}

export function needsReconsent(current: ConsentState | null, configVersion: string): boolean {
	if (!current) return true;
	return current.version !== configVersion;
}

export function buildChoicesFromState(state: ConsentState): Record<CookieCategory, boolean> {
	return {
		necessary: true,
		functional: state.functional,
		analytics: state.analytics,
		marketing: state.marketing,
	};
}

export function acceptAllChoices(): Record<CookieCategory, boolean> {
	return { necessary: true, functional: true, analytics: true, marketing: true };
}

export function rejectAllChoices(): Record<CookieCategory, boolean> {
	return { necessary: true, functional: false, analytics: false, marketing: false };
}

function getCookieValue(name: string): string | null {
	const cookies = document.cookie.split(";");
	for (const cookie of cookies) {
		const [key, ...rest] = cookie.split("=");
		if (key?.trim() === name) {
			return rest.join("=") || null;
		}
	}
	return null;
}

function isValidConsentState(value: unknown): value is ConsentState {
	if (typeof value !== "object" || value === null) return false;

	const obj = value as Record<string, unknown>;

	if (typeof obj.stamp !== "string") return false;
	if (typeof obj.action !== "string") return false;
	if (!["accept_all", "reject_all", "custom"].includes(obj.action)) return false;
	if (typeof obj.version !== "string") return false;
	if (typeof obj.utc !== "number") return false;

	for (const cat of ALL_CATEGORIES) {
		if (typeof obj[cat] !== "boolean") return false;
	}

	return true;
}
